import { useCallback, useEffect, useMemo, useState } from 'react';
import { Armchair, Bank, Check, CreditCard, Door, FloppyDisk, Handshake, HouseLine, Plus, Ruler, Trash, Wallet, X } from '@phosphor-icons/react';
import {
  Auswahl, BestaetigenDialog, FeldRaster, ImmoButton, InnenKarte, KartenTitel, LivePreviewCard, SegmentedControl, TextFeld, ToastPill, useToastPill,
} from '../../../../../components/immobilien/ui';
import { supabase } from '../../../../../lib/supabase/client';
import { eur, qm } from '../../../../../lib/immobilien/format';
import { meldeObjektGeaendert } from '../../../../../lib/immobilien/useObjektKontext';
import { AUSSTATTUNG_OPTIONEN, ausstattungListe, ausstattungUmschalten, kaufKennzahlen } from '../../../../../lib/immobilien/einheitDetail';
import {
  EINHEIT_STATUS, EINHEIT_TYPEN, WOHNUNGSARTEN, bezeichnungFehler, entwurfAusEinheit, payloadEinheit, type EinheitEntwurf, type EinheitFeld,
} from '../../../../../lib/immobilien/einheitFormular';
import { leseZahl } from '../../../../../lib/immobilien/mietverhaeltnisFormular';
import { hausAdresse, sortiereHaeuser, type Haus } from '../../../../../lib/objekte/haeuser';
import { kontaktName } from '../../../../../lib/objekte/objektKontakte';
import { MERKMAL_ICON } from '../EinheitWidgets';
import { useEinheitSeite } from '../EinheitSeitenKontext';

export type EinheitReiter = 'einheit' | 'ausstattung' | 'kauf' | 'finanzierung' | 'abrechnung';

const optionen = (liste: readonly string[], wert: string) =>
  (wert && !liste.includes(wert) ? [...liste, wert] : liste).map(x => ({ key: x, label: x }));

/**
 * „Einheit bearbeiten“ im neuen Design – ersetzt das bisherige Einheiten-Formular mit allen Feldern
 * (Wohnungsdetails, Ausstattung, Kaufpreis, Finanzierung, Abrechnungsdaten). Mieterdaten und Miete
 * bearbeitet „Mietverhältnis bearbeiten“ (dort mit Mietständen). Beim Umbenennen werden Mieter,
 * Zähler und Mietverträge mit der alten Bezeichnung mitgezogen.
 */
export function EinheitBearbeitenBereich({ startReiter, onFertig, registriereVerlassen }: {
  startReiter: EinheitReiter;
  onFertig: () => void;
  registriereVerlassen: (f: (() => void) | null) => void;
}) {
  const s = useEinheitSeite();
  const roh = s.daten.einheit;
  const start = useMemo(() => entwurfAusEinheit(roh), [roh]);
  const [x, setX] = useState<EinheitEntwurf>(start);
  const [reiter, setReiter] = useState<EinheitReiter>(startReiter);
  const [haeuser, setHaeuser] = useState<Haus[]>([]);
  const [eigenes, setEigenes] = useState('');
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [verlassen, setVerlassen] = useState(false);
  const [toast, zeige] = useToastPill();
  const set = (f: EinheitFeld) => (v: string) => setX(a => ({ ...a, [f]: v }));

  useEffect(() => {
    let aktiv = true;
    supabase.from('objekt_haeuser').select('*').eq('objekt_id', s.daten.objekt.id)
      .then(({ data }) => { if (aktiv) setHaeuser(sortiereHaeuser((data as Haus[] | null) ?? [])); });
    return () => { aktiv = false; };
  }, [s.daten.objekt.id]);

  const geaendert = JSON.stringify(x) !== JSON.stringify(start);
  const abbrechen = useCallback(() => (geaendert ? setVerlassen(true) : onFertig()), [geaendert, onFertig]);
  useEffect(() => { registriereVerlassen(abbrechen); return () => registriereVerlassen(null); }, [abbrechen, registriereVerlassen]);

  const speichere = async () => {
    setFehler(null);
    setSpeichert(true);
    try {
      const alt = (roh.bezeichnung ?? '').trim();
      const neu = x.bezeichnung.trim();
      if (neu.toLowerCase() !== alt.toLowerCase()) {
        const andere = await supabase.from('objekt_einheiten').select('bezeichnung').eq('objekt_id', s.daten.objekt.id).neq('id', roh.id);
        if (andere.error) throw new Error(andere.error.message);
        const f = bezeichnungFehler(neu, ((andere.data ?? []) as { bezeichnung: string | null }[]).map(a => a.bezeichnung));
        if (f) { setFehler(f); setReiter('einheit'); return; }
      } else if (!neu) { setFehler('Bitte eine Bezeichnung angeben.'); setReiter('einheit'); return; }

      // wie bisher nur Spalten, die die Zeile tatsächlich hat
      const voll = payloadEinheit(x);
      const payload = Object.fromEntries(Object.entries(voll).filter(([k]) => k in roh));
      const { error } = await supabase.from('objekt_einheiten').update(payload).eq('id', roh.id);
      if (error) throw new Error(error.message);

      // Bezeichnung geändert: Text-Bezüge mitziehen (Mieter, Zähler, Mietverträge)
      const warnungen: string[] = [];
      if (neu !== alt) {
        const [m, z, v] = await Promise.all([
          supabase.from('mieter').update({ einheit: neu }).eq('einheit_id', roh.id),
          alt ? supabase.from('zaehler').update({ einheit_bezeichnung: neu }).eq('objekt_id', s.daten.objekt.id).eq('einheit_bezeichnung', alt) : Promise.resolve({ error: null }),
          alt ? supabase.from('vertraege').update({ einheit: neu }).eq('objekt_id', s.daten.objekt.id).eq('einheit', alt) : Promise.resolve({ error: null }),
        ]);
        if (m.error) warnungen.push(`Mieter: ${m.error.message}`);
        if (z.error) warnungen.push(`Zähler: ${z.error.message}`);
        if (v.error) warnungen.push(`Verträge: ${v.error.message}`);
      }
      meldeObjektGeaendert(s.daten.objekt.id);
      if (warnungen.length) { zeige(`Gespeichert, aber nicht alles umbenannt – ${warnungen.join(' · ')}`, true); s.neuLaden('einheit'); return; }
      s.geaendert('einheit');
      onFertig();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
    } finally {
      setSpeichert(false);
    }
  };

  const merkmale = ausstattungListe(x.ausstattung);
  const eigeneMerkmale = merkmale.filter(m => !(AUSSTATTUNG_OPTIONEN as readonly string[]).includes(m));
  const merkmalDazu = () => {
    const m = eigenes.replace(/,/g, ' ').trim();
    if (m && !merkmale.includes(m)) set('ausstattung')(ausstattungUmschalten(x.ausstattung, m));
    setEigenes('');
  };
  const flaeche = leseZahl(x.wohnflaeche) ?? leseZahl(x.nutzflaeche) ?? 0;
  const kauf = kaufKennzahlen(payloadEinheit(x), s.miete.kalt);
  const haus = haeuser.find(h => h.id === x.haus_id);
  const statusLabel = EINHEIT_STATUS.find(o => o.key === x.status)?.label ?? x.status;

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <div className="flex flex-wrap items-start" style={{ gap: 16 }}>
        <div className="flex flex-col min-w-0" style={{ flex: '2 1 420px', gap: 14 }}>
          <div className="immo-ohne-scrollbar overflow-x-auto">
            <SegmentedControl ariaLabel="Bereiche der Einheit" wert={reiter} onChange={setReiter}
              segmente={[
                { key: 'einheit', label: 'Wohnungsdetails', icon: Door }, { key: 'ausstattung', label: 'Ausstattung', icon: Armchair },
                { key: 'kauf', label: 'Kaufpreis', icon: Wallet }, { key: 'finanzierung', label: 'Finanzierung', icon: Bank },
                { key: 'abrechnung', label: 'Abrechnungsdaten', icon: CreditCard },
              ]} />
          </div>
          {fehler && <span role="alert" style={{ fontSize: 13, fontWeight: 700, color: 'var(--bad)' }}>{fehler}</span>}

          {reiter === 'einheit' && (<>
            <InnenKarte>
              <KartenTitel icon={Door}>Einheit</KartenTitel>
              <FeldRaster minBreite={190}>
                <TextFeld label="Bezeichnung *" wert={x.bezeichnung} onChange={set('bezeichnung')} fehler={!x.bezeichnung.trim() ? 'Pflichtfeld' : undefined} />
                <TextFeld label="Besitzübergang" type="date" wert={x.besitzuebergang} onChange={set('besitzuebergang')} />
              </FeldRaster>
              <Auswahl label="Art der Einheit" wert={x.typ || null} onChange={set('typ')} optionen={optionen(EINHEIT_TYPEN, x.typ)} />
              <Auswahl label="Art der Wohnung" wert={x.art_wohnung || null} onChange={set('art_wohnung')} optionen={optionen(WOHNUNGSARTEN, x.art_wohnung)} />
              {x.art_wohnung && <button type="button" onClick={() => set('art_wohnung')('')} className="cursor-pointer" style={{ alignSelf: 'flex-start', border: 'none', background: 'transparent', padding: 0, fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Art der Wohnung entfernen</button>}
              <Auswahl label="Status" wert={x.status || null} onChange={set('status')}
                optionen={[...EINHEIT_STATUS, ...(x.status && !EINHEIT_STATUS.some(o => o.key === x.status) ? [{ key: x.status, label: x.status }] : [])]} />
              {haeuser.length > 0 && (
                <Auswahl label="Haus" wert={x.haus_id || 'ohne'} onChange={k => set('haus_id')(k === 'ohne' ? '' : k)}
                  optionen={[{ key: 'ohne', label: 'Ohne Haus' }, ...haeuser.map(h => ({ key: h.id, label: hausAdresse(h) ? `${h.bezeichnung} · ${hausAdresse(h)}` : h.bezeichnung }))]} />
              )}
            </InnenKarte>
            <InnenKarte>
              <KartenTitel icon={Ruler}>Eckdaten</KartenTitel>
              <FeldRaster minBreite={150}>
                <TextFeld label="Wohnfläche" wert={x.wohnflaeche} onChange={set('wohnflaeche')} inputMode="decimal" suffix="m²" />
                <TextFeld label="Nutzfläche" wert={x.nutzflaeche} onChange={set('nutzflaeche')} inputMode="decimal" suffix="m²" />
                <TextFeld label="Etage" wert={x.etage} onChange={set('etage')} />
                <TextFeld label="Anzahl der Zimmer" wert={x.zimmer} onChange={set('zimmer')} inputMode="decimal" />
                <TextFeld label="Badezimmer" wert={x.badezimmer} onChange={set('badezimmer')} inputMode="decimal" />
                <TextFeld label="Schlafzimmer" wert={x.schlafzimmer} onChange={set('schlafzimmer')} inputMode="decimal" />
              </FeldRaster>
              <label className="flex flex-col" style={{ gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Notizen</span>
                <textarea value={x.notizen} onChange={ev => set('notizen')(ev.target.value)} rows={3} placeholder="Interne Notizen zur Einheit …"
                  style={{ width: '100%', resize: 'vertical', border: 'none', outline: 'none', borderRadius: 15, padding: 14, background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)', fontSize: 15, lineHeight: 1.5, color: 'var(--ink)' }} />
              </label>
            </InnenKarte>
            <InnenKarte>
              <KartenTitel icon={Handshake}>Mieter</KartenTitel>
              <span style={{ fontSize: 13, color: 'var(--ink3)' }}>
                {s.aktuell ? `${kontaktName(s.aktuell)} – Name, Kontakt, Miete, Kaution und Status bearbeitest du im Mietverhältnis.` : 'Diese Einheit hat kein laufendes Mietverhältnis.'}
              </span>
              {s.aktuell
                ? <ImmoButton icon={Handshake} style={{ alignSelf: 'flex-start' }} disabled={geaendert} title={geaendert ? 'Erst speichern oder verwerfen' : undefined}
                    onClick={() => { if (s.aktuell) s.bearbeiteMietverhaeltnis(s.aktuell); }}>Mietverhältnis bearbeiten</ImmoButton>
                : <ImmoButton icon={Plus} style={{ alignSelf: 'flex-start' }} disabled={geaendert} title={geaendert ? 'Erst speichern oder verwerfen' : undefined}
                    onClick={() => s.oeffneBereich('mv')}>Mieter zuordnen</ImmoButton>}
              {geaendert && <span style={{ fontSize: 12, color: 'var(--warn)' }}>Erst die Änderungen an der Einheit speichern oder verwerfen.</span>}
            </InnenKarte>
          </>)}

          {reiter === 'ausstattung' && (
            <InnenKarte>
              <KartenTitel icon={Armchair} rechts={`${merkmale.length} ${merkmale.length === 1 ? 'Merkmal' : 'Merkmale'}`}>Ausstattung</KartenTitel>
              <div className="flex flex-wrap" style={{ gap: 8 }}>
                {AUSSTATTUNG_OPTIONEN.map(mk => {
                  const an = merkmale.includes(mk);
                  const IconKomp = MERKMAL_ICON[mk] ?? Check;
                  return (
                    <button key={mk} type="button" role="checkbox" aria-checked={an} onClick={() => set('ausstattung')(ausstattungUmschalten(x.ausstattung, mk))}
                      className="flex items-center cursor-pointer"
                      style={{ height: 40, padding: '0 14px', borderRadius: 13, gap: 7, border: 'none', fontSize: 13, fontWeight: an ? 800 : 700,
                        background: an ? 'var(--bg)' : 'var(--fill2)', boxShadow: an ? 'inset 0 0 0 2px var(--acc)' : 'none', color: 'var(--ink)' }}>
                      <IconKomp weight="fill" size={15} color={an ? 'var(--acc)' : 'var(--ink3)'} />{mk}
                    </button>
                  );
                })}
              </div>
              {eigeneMerkmale.length > 0 && (
                <div className="flex flex-wrap" style={{ gap: 8 }}>
                  {eigeneMerkmale.map(mk => (
                    <span key={mk} className="flex items-center" style={{ height: 32, padding: '0 6px 0 12px', borderRadius: 16, gap: 6, fontSize: 13, fontWeight: 700, background: 'var(--acc-soft)', color: 'var(--acc)' }}>
                      {mk}
                      <button type="button" aria-label={`${mk} entfernen`} onClick={() => set('ausstattung')(ausstattungUmschalten(x.ausstattung, mk))}
                        className="flex items-center justify-center cursor-pointer" style={{ width: 22, height: 22, borderRadius: '50%', border: 'none', background: 'transparent', color: 'inherit' }}><X size={12} weight="bold" /></button>
                    </span>
                  ))}
                </div>
              )}
              <form className="flex flex-wrap" style={{ gap: 8 }} onSubmit={ev => { ev.preventDefault(); merkmalDazu(); }}>
                <input value={eigenes} onChange={ev => setEigenes(ev.target.value)} placeholder="Weiteres Merkmal, z. B. Glasfaser" aria-label="Weiteres Merkmal"
                  style={{ flex: '1 1 220px', height: 44, borderRadius: 14, padding: '0 14px', border: 'none', outline: 'none', background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)', fontSize: 14, color: 'var(--ink)' }} />
                <ImmoButton type="submit" groesse={44} pille={false} icon={Plus} disabled={!eigenes.trim()}>Hinzufügen</ImmoButton>
              </form>
            </InnenKarte>
          )}

          {reiter === 'kauf' && (<>
            <InnenKarte>
              <KartenTitel icon={Wallet}>Kaufpreis</KartenTitel>
              <FeldRaster minBreite={170}>
                <TextFeld label="Objektkaufpreis" wert={x.kaufpreis} onChange={set('kaufpreis')} inputMode="decimal" suffix="€" />
                <TextFeld label="Stellplatz" wert={x.kaufpreis_stellplatz} onChange={set('kaufpreis_stellplatz')} inputMode="decimal" suffix="€" />
                <TextFeld label="Garage" wert={x.kaufpreis_garage} onChange={set('kaufpreis_garage')} inputMode="decimal" suffix="€" />
              </FeldRaster>
            </InnenKarte>
            <InnenKarte>
              <KartenTitel>Kaufnebenkosten</KartenTitel>
              <FeldRaster minBreite={170}>
                <TextFeld label="Maklerkosten" wert={x.nk_makler} onChange={set('nk_makler')} inputMode="decimal" suffix="€" />
                <TextFeld label="Notarkosten" wert={x.nk_notar} onChange={set('nk_notar')} inputMode="decimal" suffix="€" />
                <TextFeld label="Anwaltskosten" wert={x.nk_anwalt} onChange={set('nk_anwalt')} inputMode="decimal" suffix="€" />
                <TextFeld label="Grunderwerbsteuer" wert={x.nk_grunderwerbsteuer} onChange={set('nk_grunderwerbsteuer')} inputMode="decimal" suffix="€" />
                <TextFeld label="Eintragungskosten" wert={x.nk_eintragung} onChange={set('nk_eintragung')} inputMode="decimal" suffix="€" />
                <TextFeld label="Gutachter" wert={x.nk_gutachter} onChange={set('nk_gutachter')} inputMode="decimal" suffix="€" />
              </FeldRaster>
            </InnenKarte>
          </>)}

          {reiter === 'finanzierung' && (
            <InnenKarte>
              <KartenTitel icon={Bank}>Finanzierung</KartenTitel>
              <FeldRaster minBreite={170}>
                <TextFeld label="Name der Bank" wert={x.finanz_bank} onChange={set('finanz_bank')} />
                <TextFeld label="Ansprechpartner" wert={x.finanz_ansprechpartner} onChange={set('finanz_ansprechpartner')} />
                <TextFeld label="Darlehenssumme" wert={x.finanz_darlehenssumme} onChange={set('finanz_darlehenssumme')} inputMode="decimal" suffix="€" />
                <TextFeld label="Zinsbindungsfrist" type="date" wert={x.finanz_zinsbindung} onChange={set('finanz_zinsbindung')} />
                <TextFeld label="Zinssatz" wert={x.finanz_zinssatz} onChange={set('finanz_zinssatz')} inputMode="decimal" suffix="%" />
                <TextFeld label="Anfängl. Tilgung" wert={x.finanz_tilgung} onChange={set('finanz_tilgung')} inputMode="decimal" suffix="%" />
                <TextFeld label="Annuität" wert={x.finanz_annuitaet} onChange={set('finanz_annuitaet')} inputMode="decimal" suffix="%" />
              </FeldRaster>
            </InnenKarte>
          )}

          {reiter === 'abrechnung' && (
            <InnenKarte>
              <KartenTitel icon={CreditCard}>Abrechnungsdaten</KartenTitel>
              <FeldRaster minBreite={190}>
                <TextFeld label="Kontoinhaber" wert={x.bank_kontoinhaber} onChange={set('bank_kontoinhaber')} />
                <TextFeld label="IBAN" wert={x.bank_iban} onChange={set('bank_iban')} placeholder="DE00 0000 0000 0000 0000 00" />
                <TextFeld label="BIC" wert={x.bank_bic} onChange={set('bank_bic')} />
              </FeldRaster>
              <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Daten des Mieteingangskontos – sie werden in der Betriebskostenabrechnung als Platzhalter ausgefüllt.</span>
            </InnenKarte>
          )}
        </div>

        <div className="flex flex-col" style={{ flex: '1 1 250px', maxWidth: 320, minWidth: 0, gap: 12, position: 'sticky', top: 16 }}>
          <LivePreviewCard>
            <div className="flex items-center min-w-0" style={{ gap: 12 }}>
              <span className="flex items-center justify-center shrink-0" style={{ width: 46, height: 46, borderRadius: 15, background: 'var(--acc-soft)' }}><Door weight="fill" size={22} color="var(--acc)" /></span>
              <div className="flex flex-col min-w-0">
                <span className="truncate" style={{ fontSize: 15, fontWeight: 800 }}>{x.bezeichnung.trim() || 'Ohne Bezeichnung'}</span>
                <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{[x.typ, x.art_wohnung, x.etage].filter(Boolean).join(' · ') || 'Einheit'}</span>
              </div>
            </div>
            <div className="flex flex-col" style={{ gap: 6, paddingTop: 12, borderTop: '1px solid var(--line)', fontSize: 12, color: 'var(--ink2)' }}>
              <span className="flex items-center" style={{ gap: 8 }}><Ruler size={14} />{flaeche ? qm(flaeche) : 'Fläche fehlt'}{x.zimmer ? ` · ${x.zimmer} Zimmer` : ''}</span>
              <span className="flex items-center" style={{ gap: 8 }}><Check size={14} />{statusLabel || 'Status offen'} · {merkmale.length} Merkmale</span>
              {haus && <span className="flex items-center" style={{ gap: 8 }}><HouseLine size={14} />{haus.bezeichnung}</span>}
              <span className="flex items-center" style={{ gap: 8 }}><Wallet size={14} />{kauf.kaufpreis ? `Kaufpreis ${eur(kauf.kaufpreis, { nachkomma: 0 })}` : 'Kein Kaufpreis'}</span>
              {kauf.darlehen > 0 && <span className="flex items-center" style={{ gap: 8 }}><Bank size={14} />Darlehen {eur(kauf.darlehen, { nachkomma: 0 })}{kauf.rateMonat ? ` · ${eur(kauf.rateMonat)}/Monat` : ''}</span>}
            </div>
          </LivePreviewCard>
          <div className="flex flex-col" style={{ gap: 8 }}>
            <ImmoButton art="akzent" icon={FloppyDisk} disabled={speichert || !geaendert} onClick={() => { void speichere(); }}>{speichert ? 'Speichert …' : 'Speichern'}</ImmoButton>
            <ImmoButton onClick={abbrechen}>{geaendert ? 'Abbrechen' : 'Schließen'}</ImmoButton>
            <span className="text-center" style={{ fontSize: 11, color: geaendert ? 'var(--warn)' : 'var(--ink3)' }}>{geaendert ? 'Ungespeicherte Änderungen' : 'Keine ungespeicherten Änderungen'}</span>
          </div>
        </div>
      </div>

      {verlassen && (
        <BestaetigenDialog titel="Änderungen verwerfen?" text="Die ungespeicherten Änderungen an der Einheit gehen verloren."
          bestaetigenLabel="Verwerfen" icon={Trash} onBestaetigen={() => { setVerlassen(false); onFertig(); }} onAbbrechen={() => setVerlassen(false)} />
      )}
    </>
  );
}
